import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, Calendar, Users, X } from "lucide-react";
import { Mentor } from "@/types/Mentor.ts";
import { MenteeWithPreferenceAndSuggestionsScore } from "@/types/Mentee.ts";
import { cn } from "@/lib/utils";
import { WhatsAppIcon } from "@/components/Icons.tsx";
import { getLocalizedFieldName } from "@/utils/translation-helper.ts";
import pluralize from "pluralize";
import { getImgSrc } from "@/utils/file-upload.ts";

/** UTC offsets (in hours) for mentor timezone abbreviations. */
const TZ_OFFSET_HOURS: Record<string, number> = {
    PDT: -7,
    MDT: -6,
    CDT: -5,
    EDT: -4,
    ADT: -3,
    NDT: -2.5,
    PST: -8,
    MST: -7,
    CST: -6,
    EST: -5,
    AST: -4,
    NST: -3.5,
};

const EVENT_CHIP_STYLES = [
    { chip: "bg-brand-arctic/10 hover:bg-brand-arctic/20", dot: "bg-brand-arctic" },
    { chip: "bg-brand-spring/10 hover:bg-brand-spring/20", dot: "bg-brand-spring" },
    { chip: "bg-brand-pink/10 hover:bg-brand-pink/20", dot: "bg-brand-pink" },
    { chip: "bg-brand-tangerine/10 hover:bg-brand-tangerine/20", dot: "bg-brand-tangerine" },
] as const;

/**
 * Convert a time value to Eastern Time (America/Toronto, auto-DST).
 *
 * Both ISO strings ("1970-01-01T17:00:00.000Z") and plain "HH:mm" strings
 * represent the mentor's **local** time. The "Z" suffix on ISO strings is a
 * Prisma serialisation artefact for `time` columns — it does NOT mean UTC.
 *
 * Steps:
 *   1. Parse hours/minutes from the string (always mentor-local).
 *   2. Subtract the mentor's UTC offset to get real UTC.
 *   3. Use Intl to convert that UTC instant to Eastern Time.
 */
function convertToEastern(
    date: Date,
    timeStr: string,
    mentorTz: string
): { hours: number; minutes: number } {
    // 1. Parse mentor-local hours/minutes
    let localHours: number;
    let localMinutes: number;

    if (timeStr.includes("T")) {
        // ISO format from API, e.g. "1970-01-01T17:30:00.000Z"
        localHours = parseInt(timeStr.slice(11, 13), 10);
        localMinutes = parseInt(timeStr.slice(14, 16), 10);
    } else {
        const parts = timeStr.split(":");
        localHours = parseInt(parts[0], 10);
        localMinutes = parseInt(parts[1] ?? "0", 10);
    }

    // 2. Convert mentor-local → UTC
    //    offset is negative for west-of-UTC zones (e.g. MDT = -6)
    //    UTC = local − offset  →  17:30 − (−6) = 23:30 UTC
    const mentorOffset = TZ_OFFSET_HOURS[mentorTz] ?? -4; // default EDT
    const utcHours = localHours - mentorOffset;

    // 3. Build a UTC instant on the correct calendar date
    const utcDate = new Date(
        Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), utcHours, localMinutes)
    );

    // 4. Format in Eastern Time (handles DST automatically)
    const eastern = new Intl.DateTimeFormat("en-US", {
        timeZone: "America/Toronto",
        hour: "numeric",
        minute: "2-digit",
        hour12: false,
    }).format(utcDate);

    const [eH, eM] = eastern.split(":").map(Number);
    return { hours: eH === 24 ? 0 : eH, minutes: eM };
}

function formatTime12h(hours: number, minutes: number): string {
    const period = hours >= 12 ? "PM" : "AM";
    const h12 = hours % 12 || 12;
    const m = minutes.toString().padStart(2, "0");
    return `${h12}:${m} ${period}`;
}

type CalendarEvent = {
    mentorId: number;
    mentorName: string;
    mentorFirstName: string;
    mentorInitials: string;
    mentorPhoto: string | undefined;
    date: Date;
    startTime: string;
    endTime: string;
    dateKey: string;
    sessionIndex: number;
    sessionId: number | null;
    hasNote: boolean;
    mentor: Mentor;
};

type MentorshipCalendarProps = {
    mentors: Mentor[];
};

export function MentorshipCalendar({ mentors }: MentorshipCalendarProps) {
    const { t, i18n } = useTranslation();
    const [currentMonth, setCurrentMonth] = useState(() => {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth(), 1);
    });
    const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);

    const events = useMemo(() => {
        const result: CalendarEvent[] = [];
        for (const mentor of mentors) {
            if (!mentor.availabilities?.length) continue;
            const tz = mentor.timezone || "EDT";
            const name = mentor.user.fullName || `${mentor.user.firstName} ${mentor.user.lastName}`;
            const firstName = (mentor.user.firstName ?? "").trim() || name.trim().split(/\s+/)[0] || "Mentor";
            const initials = `${mentor.user.firstName?.[0] ?? ""}${mentor.user.lastName?.[0] ?? ""}`;
            const photo = mentor.user.profilePictureSrc
                ? getImgSrc(mentor.user.profilePictureSrc)
                : undefined;

            const sorted = [...mentor.availabilities].sort((a, b) => {
                const da = new Date(a.availableOn).getTime();
                const db = new Date(b.availableOn).getTime();
                if (da !== db) return da - db;
                return String(a.availableFrom).localeCompare(String(b.availableFrom));
            });

            sorted.forEach((avail, idx) => {
                const rawDate = new Date(avail.availableOn);
                const date = new Date(rawDate.getTime() + rawDate.getTimezoneOffset() * 60 * 1000);
                const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

                const start = convertToEastern(date, avail.availableFrom, tz);
                const end = convertToEastern(date, avail.availableTill, tz);

                const session = avail.mentorshipSession;

                result.push({
                    mentorId: mentor.id,
                    mentorName: name,
                    mentorFirstName: firstName,
                    mentorInitials: initials,
                    mentorPhoto: photo,
                    date,
                    startTime: formatTime12h(start.hours, start.minutes),
                    endTime: formatTime12h(end.hours, end.minutes),
                    dateKey,
                    sessionIndex: idx + 1,
                    sessionId: session?.id ?? null,
                    hasNote: typeof session?.note === "string" && session.note.trim().length > 0 && session.noteIsPrivate !== true,
                    mentor,
                });
            });
        }
        return result;
    }, [mentors]);

    const eventsByDate = useMemo(() => {
        const map = new Map<string, CalendarEvent[]>();
        for (const event of events) {
            const arr = map.get(event.dateKey) ?? [];
            arr.push(event);
            map.set(event.dateKey, arr);
        }
        return map;
    }, [events]);

    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfWeek = new Date(year, month, 1).getDay();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

    const prevMonth = () => setCurrentMonth(new Date(year, month - 1, 1));
    const nextMonth = () => setCurrentMonth(new Date(year, month + 1, 1));
    const goToday = () => {
        const now = new Date();
        setCurrentMonth(new Date(now.getFullYear(), now.getMonth(), 1));
    };

    const monthLabel = currentMonth.toLocaleDateString(i18n.language, { month: "long", year: "numeric" });
    const dayHeaders = Array.from({ length: 7 }, (_, i) => {
        const d2 = new Date(2024, 0, 7 + i);
        return d2.toLocaleDateString(i18n.language, { weekday: "short" });
    });

    const cells: { day: number | null; dateKey: string | null }[] = [];
    for (let i = 0; i < firstDayOfWeek; i++) cells.push({ day: null, dateKey: null });
    for (let d = 1; d <= daysInMonth; d++) {
        const dateKey = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
        cells.push({ day: d, dateKey });
    }
    while (cells.length % 7 !== 0) cells.push({ day: null, dateKey: null });

    const affinityField = getLocalizedFieldName("name");

    return (
        <Card>
            <CardHeader>
                <div className="flex items-center justify-between">
                    <CardTitle>{t("dashboard.calendarTitle")}</CardTitle>
                    <span className="text-xs text-muted-foreground">{t("dashboard.calendarTimezone")}</span>
                </div>
            </CardHeader>
            <CardContent className="space-y-3">
                {/* Month navigation */}
                <div className="flex items-center justify-between">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={prevMonth}>
                        <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold capitalize">{monthLabel}</span>
                        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={goToday}>
                            {t("dashboard.calendarToday")}
                        </Button>
                    </div>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={nextMonth}>
                        <ChevronRight className="h-4 w-4" />
                    </Button>
                </div>

                {/* Day headers */}
                <div className="grid grid-cols-7 text-center text-xs font-medium text-muted-foreground">
                    {dayHeaders.map((d, i) => (
                        <div key={i} className="py-1">{d}</div>
                    ))}
                </div>

                {/* Calendar grid */}
                <div className="grid grid-cols-7 border-t border-l border-border">
                    {cells.map((cell, idx) => {
                        const isToday = cell.dateKey === todayKey;
                        const dayEvents = cell.dateKey ? eventsByDate.get(cell.dateKey) ?? [] : [];
                        const isPast = cell.day != null && cell.dateKey != null && cell.dateKey < todayKey;

                        return (
                            <div
                                key={idx}
                                className={cn(
                                    "min-h-[80px] border-r border-b border-border p-1 text-xs",
                                    cell.day == null && "bg-bg-elevated/30",
                                    isPast && "opacity-50",
                                    isToday && "bg-accent/5",
                                )}
                            >
                                {cell.day != null && (
                                    <>
                                        <div className={cn(
                                            "mb-1 text-right text-xs",
                                            isToday
                                                ? "inline-flex float-right h-5 w-5 items-center justify-center rounded-full bg-accent text-bg-card font-bold"
                                                : "text-text-2"
                                        )}>
                                            {cell.day}
                                        </div>
                                        <div className="clear-both space-y-0.5">
                                            {dayEvents.slice(0, 3).map((event, eIdx) => {
                                                const style = EVENT_CHIP_STYLES[event.mentorId % EVENT_CHIP_STYLES.length];
                                                return (
                                                    <button
                                                        key={eIdx}
                                                        type="button"
                                                        onClick={() => setSelectedEvent(event)}
                                                        className={cn(
                                                            "flex w-full items-center gap-1 rounded px-1 py-0.5 transition-colors truncate text-left",
                                                            style.chip
                                                        )}
                                                        title={`${event.mentorName}: ${event.startTime} – ${event.endTime}`}
                                                    >
                                                        <span
                                                            className={cn("h-4 w-4 rounded text-[9px] font-bold flex items-center justify-center shrink-0 text-bg-card", style.dot)}
                                                            aria-label={`Session ${event.sessionIndex}`}
                                                        >
                                                            {event.sessionIndex}
                                                        </span>
                                                        {event.mentorPhoto ? (
                                                            <img
                                                                src={event.mentorPhoto}
                                                                alt={event.mentorName}
                                                                className="h-4 w-4 rounded-full object-cover shrink-0"
                                                            />
                                                        ) : (
                                                            <span className="h-4 w-4 rounded-full bg-bg-elevated text-[8px] font-bold flex items-center justify-center shrink-0 text-text-2">
                                                                {event.mentorInitials}
                                                            </span>
                                                        )}
                                                        <span className="truncate text-text-1">
                                                            {event.mentorFirstName} – {event.startTime}
                                                        </span>
                                                    </button>
                                                );
                                            })}
                                            {dayEvents.length > 3 && (
                                                <div className="text-[10px] text-muted-foreground pl-1">
                                                    +{dayEvents.length - 3} {t("common.more", "more")}
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Session detail popover */}
                {selectedEvent && (
                    <SessionGroupCard
                        event={selectedEvent}
                        affinityField={affinityField}
                        onClose={() => setSelectedEvent(null)}
                        t={t}
                        i18nLanguage={i18n.language}
                    />
                )}
            </CardContent>
        </Card>
    );
}

/* ---------- Session Group Card (shown on calendar event click) ---------- */

type SessionGroupCardProps = {
    event: CalendarEvent;
    affinityField: string;
    onClose: () => void;
    t: TFunction;
    i18nLanguage: string;
};

function SessionGroupCard({ event, affinityField, onClose, t, i18nLanguage }: SessionGroupCardProps) {
    const mentor = event.mentor;
    const menteeCount = mentor.mentees?.length ?? 0;
    const affinityName = mentor.affinityGroup
        ? (mentor.affinityGroup as Record<string, unknown>)[affinityField] as string ?? mentor.affinityGroup.name
        : null;
    const sessionDate = event.date.toLocaleDateString(i18nLanguage, { weekday: "long", month: "long", day: "numeric" });

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
            <Card className="w-full max-w-sm shadow-lg animate-in fade-in-0 zoom-in-95" onClick={(e) => e.stopPropagation()}>
                <div className="p-4 space-y-3">
                    {/* Close button */}
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-text-2 uppercase tracking-wide">
                            {t("dashboard.calendarSession", "Session")} #{event.sessionIndex}
                        </span>
                        <button type="button" onClick={onClose} className="rounded p-1 hover:bg-bg-elevated transition-colors">
                            <X className="h-4 w-4 text-text-2" />
                        </button>
                    </div>

                    {/* Mentor header */}
                    <div className="flex items-start gap-3">
                        {mentor.user.profilePictureSrc ? (
                            <img
                                src={getImgSrc(mentor.user.profilePictureSrc)}
                                alt={mentor.user.fullName}
                                className="h-12 w-12 rounded-full object-cover shrink-0 border border-border"
                            />
                        ) : (
                            <div className="h-12 w-12 rounded-full bg-bg-elevated border border-border flex items-center justify-center shrink-0">
                                <span className="text-sm font-semibold text-text-2">
                                    {mentor.user.firstName?.[0]}{mentor.user.lastName?.[0]}
                                </span>
                            </div>
                        )}
                        <div className="min-w-0 flex-1">
                            <div className="font-semibold text-text-1 truncate">{mentor.user.fullName}</div>
                            {mentor.title && (
                                <div className="text-xs text-text-2 truncate">
                                    {(mentor as Record<string, unknown>)[getLocalizedFieldName("title")] as string ?? mentor.title}
                                </div>
                            )}
                            {mentor.university && (
                                <div className="text-xs text-text-2 truncate">
                                    {(mentor as Record<string, unknown>)[getLocalizedFieldName("university")] as string ?? mentor.university}
                                </div>
                            )}
                        </div>
                        {mentor.isOnWhatsApp && (
                            <span title={t("mentor.availableOnWhatsApp")} className="shrink-0 mt-0.5 inline-flex">
                                <WhatsAppIcon className="h-5 w-5 text-[#25D366]" title={t("mentor.availableOnWhatsApp")} />
                            </span>
                        )}
                    </div>

                    {/* Badges */}
                    <div className="flex flex-wrap items-center gap-2">
                        {affinityName && (
                            <Badge className="rounded-full px-3 py-1 text-xs font-semibold bg-accent text-bg-card border-0 shadow-sm">
                                {affinityName}
                            </Badge>
                        )}
                    </div>

                    {/* Session date & time (Eastern) */}
                    <div className="flex items-center gap-1.5 rounded-md bg-springGreen/10 px-2.5 py-1.5 text-xs font-medium text-springGreen">
                        <Calendar className="h-3.5 w-3.5 shrink-0" />
                        <span>{sessionDate} · {event.startTime} – {event.endTime} ET</span>
                    </div>

                    {/* Note indicator */}
                    {event.hasNote && (
                        <p className="text-xs text-text-2 italic">{t("dashboard.calendarHasNote", "Mentor has left a session note")}</p>
                    )}

                    {/* Mentee avatars */}
                    {menteeCount > 0 && (
                        <div className="flex items-center gap-2 pt-1 border-t border-border">
                            <div className="flex -space-x-2">
                                {mentor.mentees?.slice(0, 5).map((mentee: MenteeWithPreferenceAndSuggestionsScore) => (
                                    mentee.user.profilePictureSrc ? (
                                        <img
                                            key={mentee.id}
                                            src={getImgSrc(mentee.user.profilePictureSrc)}
                                            alt={mentee.user.fullName}
                                            title={mentee.user.fullName}
                                            className="h-7 w-7 rounded-full border-2 border-bg-card object-cover"
                                        />
                                    ) : (
                                        <div
                                            key={mentee.id}
                                            title={mentee.user.fullName}
                                            className="h-7 w-7 rounded-full border-2 border-bg-card bg-bg-elevated flex items-center justify-center"
                                        >
                                            <span className="text-[10px] font-medium text-text-2">
                                                {mentee.user.firstName?.[0]}{mentee.user.lastName?.[0]}
                                            </span>
                                        </div>
                                    )
                                ))}
                                {menteeCount > 5 && (
                                    <div className="h-7 w-7 rounded-full border-2 border-bg-card bg-bg-elevated flex items-center justify-center">
                                        <span className="text-[10px] font-medium text-text-2">+{menteeCount - 5}</span>
                                    </div>
                                )}
                            </div>
                            <div className="flex items-center gap-1 text-xs text-text-2 ml-auto">
                                <Users className="h-3.5 w-3.5" />
                                <span>{pluralize("mentee", menteeCount, true)}</span>
                            </div>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="grid grid-cols-2 gap-2 pt-1">
                        <Button variant="outline" size="sm" asChild>
                            <Link to={`/mentors/${mentor.id}/mentees`}>
                                {t("dashboard.viewGroup", "View group")}
                            </Link>
                        </Button>

                        {event.sessionId ? (
                            <Button size="sm" asChild>
                                <Link to={`/session-attendance?sessionId=${event.sessionId}`}>
                                    {t("dashboard.viewSession", "View session")}
                                </Link>
                            </Button>
                        ) : (
                            <Button size="sm" disabled title={t("dashboard.calendarSessionNotCreated", "Session record will be created after the mentor selection deadline")}>
                                {t("dashboard.viewSession", "View session")}
                            </Button>
                        )}
                    </div>

                    {!event.sessionId && (
                        <div className="rounded-md bg-bg-elevated px-3 py-2 text-center text-xs text-text-2">
                            {t("dashboard.calendarSessionNotCreated", "Session record will be created after the mentor selection deadline")}
                        </div>
                    )}
                </div>
            </Card>
        </div>
    );
}
